import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { auth, db } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  where,
  getDocs,
  collection,
  query,
} from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";

const Navbar: React.FC = () => {
  const [user] = useAuthState(auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    name?: string;
    role?: string;
  } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (user) {
        try {
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("uid", "==", user.uid));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            console.log("User data from Firestore:", userData);
            setUserInfo(userData as { name?: string; role?: string });
          } else {
            console.warn("No user document found for UID:", user.uid);
            setUserInfo(null);
          }
        } catch (err) {
          console.error("Error fetching user info:", err);
          setUserInfo(null);
        }
      } else {
        setUserInfo(null);
      }
    };

    fetchUserInfo();
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <nav
      className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? "shadow-lg py-2" : "shadow-md py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex-shrink-0 flex items-center group"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  setIsMenuOpen(false);
                  // After navigation to home, ensure we end up at the top
                  setTimeout(
                    () => window.scrollTo({ top: 0, behavior: "smooth" }),
                    0
                  );
                }
              }}
            >
              {/* Logo */}
              <div className="h-16 w-16 rounded-full flex items-center justify-center mr-2 group-hover:opacity-80 transition">
                <img src="assets/images/logo.png" alt="" />
              </div>
              <span className="text-2xl font-bold text-blue-800 group-hover:text-blue-600 transition">
                Maraz Sir
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-6">
                <span className="text-[16px] font-semibold text-green-950">
                  Hello! {userInfo?.name || user?.displayName || "User"}
                </span>
                <button
                  className="bg-blue-800 text-[16px] text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition font-semibold mr-2"
                  onClick={() => {
                    if (userInfo?.role === "admin") {
                      navigate("/admin");
                    } else {
                      navigate("/student-dashboard");
                    }
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="bg-red-600 text-[16px] text-white px-4 py-2 rounded shadow hover:bg-red-500 transition font-semibold"
                  onClick={handleSignOut}
                >
                  Logout
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
                >
                  Home
                </Link>
                <a
                  href="#about"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("about")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  About
                </a>
                <a
                  href="#batches"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("batches")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Batches
                </a>
                <a
                  href="#success"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("success")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Success
                </a>
                <a
                  href="#resources"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("resources")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Resources
                </a>
                <div className="flex space-x-2">
                  <Link
                    to="/login"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition duration-300"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition duration-300"
                  >
                    Sign Up
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-500 hover:bg-gray-100 focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/"
              className="block text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <a
              href="#about"
              className="block text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("about")
                  ?.scrollIntoView({ behavior: "smooth" });
                setIsMenuOpen(false);
              }}
            >
              About
            </a>
            <a
              href="#batches"
              className="block text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("batches")
                  ?.scrollIntoView({ behavior: "smooth" });
                setIsMenuOpen(false);
              }}
            >
              Batches
            </a>
            <a
              href="#success"
              className="block text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("success")
                  ?.scrollIntoView({ behavior: "smooth" });
                setIsMenuOpen(false);
              }}
            >
              Success
            </a>
            <a
              href="#resources"
              className="block text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md font-medium"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("resources")
                  ?.scrollIntoView({ behavior: "smooth" });
                setIsMenuOpen(false);
              }}
            >
              Resources
            </a>

            {user ? (
              <div className="space-y-2 mt-2">
                <div className="px-3 py-2 text-gray-700 font-semibold">
                  Hello! {userInfo?.name || user?.displayName || "User"}
                </div>
                <button
                  className="w-full bg-blue-800 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold transition duration-300"
                  onClick={() => {
                    if (userInfo?.role === "admin") {
                      navigate("/admin");
                    } else {
                      navigate("/student-dashboard");
                    }
                    setIsMenuOpen(false);
                  }}
                >
                  Dashboard
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-medium transition duration-300"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="space-y-2 mt-2">
                <Link
                  to="/login"
                  className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition duration-300 text-center"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="block w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition duration-300 text-center"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
